import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Trash2, Plus, LogOut, Save } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = ["Logements", "Témoignages", "Demandes", "Coordonnées"];
const IN = "w-full bg-ivory border border-ink/20 px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors";

const EMPTY_LISTING = {
  slug: "", name: "", city: "", category: "", amenities: [], short_description: "",
  long_description: "", features: [], location: "", cover_image: "", images: [],
  booking_url: "", booking_platform: "Airbnb", price_per_night: 149, order: 99, published: true,
};

function ListToText(a) { return (a || []).join(", "); }
function TextToList(s) { return s.split(",").map((x) => x.trim()).filter(Boolean); }

function ListingEditor({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY_LISTING, ...initial, amenities: initial?.amenities || [], features: initial?.features || [], images: initial?.images || [] });
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="border border-ink/15 p-6 bg-sand/20 space-y-4" data-testid="listing-editor">
      <div className="grid sm:grid-cols-2 gap-4">
        <label className="text-xs text-ink/60">Nom<input className={IN} value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="editor-name" /></label>
        <label className="text-xs text-ink/60">Slug (URL)<input className={IN} value={f.slug} onChange={(e) => upd("slug", e.target.value)} data-testid="editor-slug" /></label>
        <label className="text-xs text-ink/60">Ville<input className={IN} value={f.city} onChange={(e) => upd("city", e.target.value)} /></label>
        <label className="text-xs text-ink/60">Catégorie<input className={IN} value={f.category} onChange={(e) => upd("category", e.target.value)} /></label>
        <label className="text-xs text-ink/60">Équipements (séparés par virgule)<input className={IN} value={ListToText(f.amenities)} onChange={(e) => upd("amenities", TextToList(e.target.value))} /></label>
        <label className="text-xs text-ink/60">Caractéristiques (virgule)<input className={IN} value={ListToText(f.features)} onChange={(e) => upd("features", TextToList(e.target.value))} /></label>
        <label className="text-xs text-ink/60">Image de couverture (URL)<input className={IN} value={f.cover_image} onChange={(e) => upd("cover_image", e.target.value)} /></label>
        <label className="text-xs text-ink/60">Localisation<input className={IN} value={f.location} onChange={(e) => upd("location", e.target.value)} /></label>
        <label className="text-xs text-ink/60">Plateforme<input className={IN} value={f.booking_platform} onChange={(e) => upd("booking_platform", e.target.value)} /></label>
        <label className="text-xs text-ink/60">Lien réservation<input className={IN} value={f.booking_url} onChange={(e) => upd("booking_url", e.target.value)} /></label>
        <label className="text-xs text-ink/60">Prix / nuit (€)<input type="number" className={IN} value={f.price_per_night} onChange={(e) => upd("price_per_night", parseFloat(e.target.value) || 0)} data-testid="editor-price" /></label>
        <label className="text-xs text-ink/60">Ordre<input type="number" className={IN} value={f.order} onChange={(e) => upd("order", parseInt(e.target.value) || 0)} /></label>
      </div>
      <label className="block text-xs text-ink/60">Galerie (URLs séparées par virgule)<input className={IN} value={ListToText(f.images)} onChange={(e) => upd("images", TextToList(e.target.value))} /></label>
      <label className="block text-xs text-ink/60">Description courte<textarea className={IN} rows={2} value={f.short_description} onChange={(e) => upd("short_description", e.target.value)} /></label>
      <label className="block text-xs text-ink/60">Description détaillée<textarea className={IN} rows={3} value={f.long_description} onChange={(e) => upd("long_description", e.target.value)} /></label>
      <div className="flex gap-3">
        <button onClick={() => onSave(f)} data-testid="editor-save" className="bg-ink text-ivory px-6 py-2 overline hover:bg-gold hover:text-ink transition-colors inline-flex items-center gap-2"><Save size={14} /> Enregistrer</button>
        <button onClick={onCancel} className="border border-ink px-6 py-2 overline hover:bg-ink hover:text-ivory transition-colors">Annuler</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("Logements");
  const [listings, setListings] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({});
  const [editing, setEditing] = useState(null); // listing obj or "new"
  const [newT, setNewT] = useState({ quote: "", author: "", context: "", order: 99 });

  const reloadListings = () => api.get("/listings?all=true").then((r) => setListings(r.data));
  const reloadT = () => api.get("/testimonials").then((r) => setTestimonials(r.data));

  useEffect(() => {
    if (user) {
      reloadListings(); reloadT();
      api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
      api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === null) return <div className="min-h-screen flex items-center justify-center text-ink/40">Chargement…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const saveListing = async (f) => {
    if (f._id) await api.put(`/listings/${f._id}`, f);
    else await api.post("/listings", f);
    setEditing(null); reloadListings();
  };
  const delListing = async (id) => { await api.delete(`/listings/${id}`); reloadListings(); };
  const addT = async () => { await api.post("/testimonials", newT); setNewT({ quote: "", author: "", context: "", order: 99 }); reloadT(); };
  const delT = async (id) => { await api.delete(`/testimonials/${id}`); reloadT(); };
  const delLead = async (id) => { await api.delete(`/leads/${id}`); api.get("/leads").then((r) => setLeads(r.data)); };
  const saveSettings = async () => { await api.put("/settings", settings); alert("Coordonnées enregistrées."); };

  return (
    <div className="min-h-screen bg-ivory">
      <div className="border-b border-ink/10 sticky top-0 bg-ivory/95 backdrop-blur z-10">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg">Riviera Host Collection <span className="overline text-ink/40 ml-2">Admin</span></Link>
          <button onClick={logout} data-testid="logout-btn" className="overline inline-flex items-center gap-2 hover:text-gold transition-colors"><LogOut size={15} /> Déconnexion</button>
        </div>
      </div>

      <div className="max-w-[1300px] mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-10 border-b border-ink/10">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`} className={`overline px-4 py-3 border-b-2 -mb-px transition-colors ${tab === t ? "border-gold text-ink" : "border-transparent text-ink/40 hover:text-ink"}`}>{t}</button>
          ))}
        </div>

        {tab === "Logements" && (
          <div className="space-y-4" data-testid="admin-listings">
            {!editing && <button onClick={() => setEditing("new")} data-testid="add-listing" className="bg-ink text-ivory px-6 py-3 overline inline-flex items-center gap-2 hover:bg-gold hover:text-ink transition-colors"><Plus size={15} /> Ajouter un logement</button>}
            {editing === "new" && <ListingEditor initial={{}} onSave={saveListing} onCancel={() => setEditing(null)} />}
            {listings.map((l) => (
              <div key={l._id}>
                {editing && editing._id === l._id ? (
                  <ListingEditor initial={l} onSave={saveListing} onCancel={() => setEditing(null)} />
                ) : (
                  <div className="flex items-center gap-4 border border-ink/12 p-4">
                    <img src={l.cover_image} alt={l.name} className="w-16 h-16 object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-xl">{l.name}</p>
                      <p className="text-xs text-ink/50">{l.city} · {l.category} · /{l.slug}</p>
                    </div>
                    <button onClick={() => setEditing(l)} data-testid={`edit-${l.slug}`} className="overline px-4 py-2 border border-ink hover:bg-ink hover:text-ivory transition-colors">Modifier</button>
                    <button onClick={() => delListing(l._id)} data-testid={`delete-${l.slug}`} className="text-ink/40 hover:text-destructive transition-colors"><Trash2 size={18} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "Témoignages" && (
          <div className="space-y-6" data-testid="admin-testimonials">
            <div className="border border-ink/15 p-6 bg-sand/20 space-y-3">
              <textarea className={IN} rows={2} placeholder="Citation" value={newT.quote} onChange={(e) => setNewT({ ...newT, quote: e.target.value })} data-testid="new-t-quote" />
              <input className={IN} placeholder="Auteur (ex: Propriétaire — Cannes)" value={newT.author} onChange={(e) => setNewT({ ...newT, author: e.target.value })} data-testid="new-t-author" />
              <button onClick={addT} data-testid="add-testimonial" className="bg-ink text-ivory px-6 py-2 overline inline-flex items-center gap-2 hover:bg-gold hover:text-ink transition-colors"><Plus size={14} /> Ajouter</button>
            </div>
            {testimonials.map((t) => (
              <div key={t._id} className="flex items-start gap-4 border border-ink/12 p-4">
                <div className="flex-1"><p className="font-serif text-lg">“{t.quote}”</p><p className="overline text-ink/40 mt-2">{t.author}</p></div>
                <button onClick={() => delT(t._id)} data-testid={`del-t-${t._id}`} className="text-ink/40 hover:text-destructive transition-colors"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        )}

        {tab === "Demandes" && (
          <div className="space-y-4" data-testid="admin-leads">
            {leads.length === 0 && <p className="text-ink/50">Aucune demande pour le moment.</p>}
            {leads.map((l) => (
              <div key={l._id} className="border border-ink/12 p-5">
                <div className="flex justify-between items-start">
                  <p className="font-serif text-xl">{l.first_name} {l.last_name}</p>
                  <button onClick={() => delLead(l._id)} className="text-ink/40 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                </div>
                <p className="text-sm text-ink/60 mt-1">{l.email} · {l.phone}</p>
                <p className="text-sm text-ink/60 mt-2">{l.location} · {l.property_type} · {l.bedrooms} ch. · {l.capacity} pers. · Publié: {l.already_listed || "—"}</p>
                {l.listing_link && <a href={l.listing_link} className="text-sm text-gold underline break-all" target="_blank" rel="noreferrer">{l.listing_link}</a>}
                {l.message && <p className="text-sm text-ink/70 mt-2 italic">“{l.message}”</p>}
                <p className="text-xs text-ink/30 mt-2">{new Date(l.created_at).toLocaleString("fr-FR")}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "Coordonnées" && (
          <div className="max-w-lg space-y-4" data-testid="admin-settings">
            {["email", "phone", "instagram", "whatsapp", "city"].map((k) => (
              <label key={k} className="block text-xs text-ink/60 capitalize">{k}
                <input className={IN} value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} data-testid={`setting-${k}`} />
              </label>
            ))}
            <button onClick={saveSettings} data-testid="save-settings" className="bg-ink text-ivory px-6 py-3 overline inline-flex items-center gap-2 hover:bg-gold hover:text-ink transition-colors"><Save size={15} /> Enregistrer</button>
          </div>
        )}
      </div>
    </div>
  );
}
